import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import { Typography, WithStyles } from "@material-ui/core";
import { styles } from "./Contracts.styles";
import ContractModel from "../../../../models/contract/ContractModel";
import * as ContractAction from "../../../../stores/contracts/ContractAction";
import UniversalTable from "../../../components/UniversalTable/UniversalTable";
import { IUniversalTableColumn, IUniversalTableData } from "../../../components/UniversalTable/interfaces";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../../../constants/ToastStatusEnum";
import { useTranslation } from "react-i18next";
import { selectRequesting } from "../../../../selectors/requesting/RequestingSelector";
import { Breadcrumbs } from "../../../components/Breadcrumbs/Breadcrumbs";
import { CenteredContainer } from "../../../components/CenteredContainer/CenteredContainer";
import { convertNumberToDateString, fromToString, today } from "../../../../utilities/dateTimeUtils";
import EditIcon from "@material-ui/icons/Edit";
import AddBoxIcon from "@material-ui/icons/AddBox";
import { ContractDialogType, CreateContractDialog, IContractData } from "./CreateContractDialog";
import { CsvExportButton } from "../../../components/CsvExport/CsvExportButton";

export interface IContractsViewProps extends WithStyles<typeof styles> {}

const ContractsViewPure: React.FunctionComponent<IContractsViewProps> = ({ classes }: IContractsViewProps) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const [isEditContractDialogShown, setIsEditContractDialogShown] = useState<boolean>(false);
    const [currentContract, setCurrentContract] = useState<IContractData>(null);
    const [dialogType, setDialogType] = useState<ContractDialogType>(null);
    const [selectedContractId, setSelectedContractId] = useState<number>(null);

    useEffect(() => {
        dispatch(ContractAction.requestAllContracts());
    }, [dispatch]);

    const contractsFromState = useSelector(state => state.contracts.contracts);
    const isRequesting = useSelector(state => selectRequesting(state, [ContractAction.REQUEST_ALL_CONTRACTS]));

    let contracts: IUniversalTableData;
    if (contractsFromState) {
        contracts = Object.values(contractsFromState)?.map((contract: ContractModel) => ({
            id: contract.id,
            bpId: contract.bpId,
            customerName: contract.customerName,
            contractTypeId: contract.contractTypeId,
            contractTypeName: contract.contractTypeName,
            clmMaNumber: contract.clmMaNumber,
            contractProductId: contract.contractProductId,
            contractProductName: contract.contractProductName,
            affiliates: contract.affiliates,
            timeSeriesFrom: contract.timeSeriesFrom,
            timeSeriesTo: contract.timeSeriesTo,
            remark: contract.remark,
            startDate: contract.startDate,
            endDate: contract.endDate
        }));
    }

    const handleAdd = () => {
        setDialogType("createContract");
        setIsEditContractDialogShown(true);
        setCurrentContract(null);
    };

    const handleEdit = index => {
        const contractFromTable = contracts[index];
        const contractToPass = {
            bpId: contractFromTable?.bpId,
            contractTypeId: contractFromTable?.contractTypeId,
            contractProductId: contractFromTable?.contractProductId,
            clmMaNumber: contractFromTable?.clmMaNumber,
            startDate: contractFromTable?.startDate,
            endDate: contractFromTable?.endDate,
            affiliates: contractFromTable?.affiliates,
            remark: contractFromTable?.remark,
            timeSeriesFrom: contractFromTable?.timeSeriesFrom,
            timeSeriesTo: contractFromTable?.timeSeriesTo
        };

        setDialogType("editContract");
        setCurrentContract(contractToPass);
        setSelectedContractId(index);
        setIsEditContractDialogShown(true);
    };

    const handleConfirm = (type: ContractDialogType, data: IContractData) => {
        if (type === "createContract") {
            dispatch(ContractAction.addContract(data))
                .then(() => {
                    dispatch(ContractAction.requestAllContracts());
                    enqueueSnackbar("Contract added", {
                        variant: ToastStatusEnum.Info
                    });
                })
                .catch(error =>
                    enqueueSnackbar("Contract adding failed: " + error.message, {
                        variant: ToastStatusEnum.Error
                    })
                );
        } else if (type === "editContract") {
            dispatch(ContractAction.updateContract(contracts[selectedContractId].id, data))
                .then(() => {
                    dispatch(ContractAction.requestAllContracts());
                    enqueueSnackbar("Contract edited", {
                        variant: ToastStatusEnum.Info
                    });
                })
                .catch(error =>
                    enqueueSnackbar("Contract editing failed: " + error.message, {
                        variant: ToastStatusEnum.Error
                    })
                );
            setSelectedContractId(null);
        }
        setIsEditContractDialogShown(false);
    };

    const handleClose = () => {
        setIsEditContractDialogShown(false);
    };

    const columns: IUniversalTableColumn[] = [
        {
            title: "Business Partner ID",
            field: "bpId",
            type: "text"
        },
        {
            title: "Customer Name",
            field: "customerName",
            type: "text",
            editable: "never"
        },
        {
            title: "Contract Type",
            field: "contractTypeName",
            type: "text",
            editable: "never"
        },
        {
            // GUI field was renamed from "CLM MA-number" to "SAP CRM Contract ID"
            title: "SAP CRM Contract ID",
            field: "clmMaNumber",
            type: "text"
        },
        {
            title: "Information Product",
            field: "contractProductName",
            type: "text"
        },
        {
            title: "Affiliates",
            field: "affiliates",
            type: "text"
        },
        {
            title: "Time series",
            type: "component",
            component: rowData => <div>{fromToString(rowData?.timeSeriesFrom, rowData?.timeSeriesTo)}</div>,
            customSort: (a, b) => a.timeSeriesFrom - b.timeSeriesFrom
        },
        {
            title: "Remark",
            field: "remark",
            type: "text"
        },
        {
            title: "Start Date",
            field: "startDate",
            type: "date"
        },
        {
            title: "End Date",
            field: "endDate",
            type: "date"
        },
        {
            type: "actions",
            title: t("admin.taskView.actions"),
            actions: [
                {
                    name: "editContract",
                    icon: <EditIcon />,
                    tooltip: t("general.edit"),
                    onClick: index => handleEdit(index),
                    scope: "subscriptions.users.write"
                },
                {
                    name: "addContract",
                    tableAction: true,
                    icon: <AddBoxIcon />,
                    tooltip: "Add a contract",
                    onClick: () => handleAdd(),
                    scope: "contracts.write"
                }
            ]
        }
    ];

    const exportMapping = [
        {
            label: "Business Partner ID",
            key: "bpId"
        },
        {
            label: "Customer Name",
            key: "customerName"
        },
        {
            label: "Contract Type",
            key: "contractTypeName"
        },
        {
            // GUI field was renamed from "CLM MA-number" to "SAP CRM Contract ID"
            label: "SAP CRM Contract ID",
            key: "clmMaNumber"
        },
        {
            label: "Information Product",
            key: "contractProductName"
        },
        {
            label: "Affiliates",
            key: "affiliates"
        },
        {
            label: "Time series from",
            key: "timeSeriesFrom",
            mappingFunc: (value) => convertNumberToDateString(value)
        },
        {
            label: "Time series to",
            key: "timeSeriesTo",
            mappingFunc: (value) => convertNumberToDateString(value)
        },
        {
            label: "Remark",
            key: "remark"
        },
        {
            label: "Start Date",
            key: "startDate",
            mappingFunc: (value) => convertNumberToDateString(value)
        },
        {
            label: "End Date",
            key: "endDate",
            mappingFunc: (value) => convertNumberToDateString(value)
        }
    ];

    return (
        <CenteredContainer>
            <Breadcrumbs
                items={[
                    { title: t("admin.adminPanel"), href: "/admin" },
                    { title: "Contracts", href: "/admin/contracts" },
                    { title: "Manage Contracts" }
                ]}
            />
            <div className={classes.title}>
                <Typography variant="h4">Manage Contracts</Typography>
            </div>
            <div className={classes.exportButton}>
                <CsvExportButton
                    data={contracts || []}
                    outputFilename={`contracts_${today().format("YYYYMMDD")}.csv`}
                    mappings={exportMapping}
                >
                    Export contracts
                </CsvExportButton>
            </div>
            <UniversalTable columns={columns} data={contracts} isLoading={isRequesting} searchable />
            {isEditContractDialogShown && (
                <CreateContractDialog
                    dialogType={dialogType}
                    onClose={handleClose}
                    onConfirm={handleConfirm}
                    contract={currentContract}
                />
            )}
        </CenteredContainer>
    );
};

export const ContractsView = withStyles(styles)(ContractsViewPure);
